import { AxiosResponse } from 'axios';

// Standard API response shape. Some list endpoints may include a count
// either as a top-level field next to data, or inside data as { items, count }.
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | { items: T; count?: number };
  count?: number;
  meta?: unknown;
}

/**
 * Utility function to extract data from standardized API responses
 * Handles both new standardized format and legacy format for backward compatibility
 *
 * @param {AxiosResponse} response - Axios response object
 * @returns {T} - The actual data content
 */
export function extractApiData<T>(response: AxiosResponse<ApiResponse<T> | T>): T {
  const resp = response.data as any;

  if (resp && typeof resp === 'object') {
    // Standardized format: { success, message, data, meta, [count?] }
    if ('success' in resp && 'data' in resp) {
      const payload = resp as ApiResponse<T>;

      // If payload.data is a wrapper object with { items, count }, return items for backward compatibility
      if (
        payload &&
        payload.data &&
        typeof payload.data === 'object' &&
        'items' in (payload.data as any)
      ) {
        return ((payload.data as any).items ?? []) as T;
      }
      return payload.data as T;
    }

    // Legacy or non-standard format
    // - Some endpoints may return { items: T, count } without the standard wrapper
    if ('items' in resp && Array.isArray((resp as any).items)) {
      return (resp as any).items as T;
    }
    return resp as T;
  }
  return resp as T;
}

/**
 * Extract list data and total count when available.
 * Falls back to inferring count from items.length if not provided.
 */
export function extractApiList<T>(
  response: AxiosResponse<ApiResponse<T[]> | { items: T[]; count?: number } | T[]>
): { items: T[]; count: number } {
  const resp = response.data as any;

  // Standardized wrapper
  if (resp && typeof resp === 'object' && 'success' in resp && 'data' in resp) {
    const payload = resp as ApiResponse<T[]>;
    if (payload && payload.data && typeof payload.data === 'object' && 'items' in (payload.data as any)) {
      const items = ((payload.data as any).items ?? []) as T[];
      const count =
        (payload.data as any).count ?? (typeof payload.count === 'number' ? payload.count : items.length);
      return { items, count };
    }
    const items = (payload.data as T[]) ?? [];
    const count = typeof payload.count === 'number' ? payload.count : items.length;
    return { items, count };
  }

  // Unwrapped list with optional { items, count }
  if (resp && typeof resp === 'object' && 'items' in resp && Array.isArray(resp.items)) {
    const items = (resp.items as T[]) ?? [];
    const count = typeof resp.count === 'number' ? resp.count : items.length;
    return { items, count };
  }

  // Plain array fallback
  const items = (Array.isArray(resp) ? (resp as T[]) : []) as T[];
  return { items, count: items.length };
}

/**
 * Utility function to check if API response indicates success
 *
 * @param {AxiosResponse} response - Axios response object
 * @returns {boolean} - Whether the operation was successful
 */
export function isApiSuccess(response: AxiosResponse<ApiResponse<unknown>>): boolean {
  if (response.data && typeof response.data === 'object' && 'success' in response.data) {
    return response.data.success;
  }
  // For legacy responses, assume success if status is 2xx
  return response.status >= 200 && response.status < 300;
}

/**
 * Utility function to get error message from API response
 *
 * @param {AxiosError} error - Axios error object
 * @returns {string} - Error message
 */
import type { AxiosError } from 'axios';

/**
 * Utility function to get error message from API response
 *
 * @param {AxiosError<unknown> | Error} error - Error object
 * @returns {string} - Error message
 */
export function getApiErrorMessage(error: AxiosError<unknown> | Error): string {
  if ('response' in error && error.response?.data) {
    const responseData = error.response.data as Record<string, unknown>;
    if ('message' in responseData) {
      return String(responseData.message);
    }
    if (
      'error' in responseData &&
      typeof responseData.error === 'object' &&
      responseData.error !== null
    ) {
      const errObj = responseData.error as Record<string, unknown>;
      if ('message' in errObj) {
        return String(errObj.message);
      }
    }
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred';
}
